import { useEffect, useState } from "react";
import { normalize } from "./normalize";
import { DATASETS, type DatasetDescriptor, type DatasetIndex, type Entity, type Mode } from "./types";

export interface DatasetData {
  descriptor: DatasetDescriptor;
  raw: DatasetIndex;
  /** map from normalized name → all entities with that name */
  byNormalizedName: Map<string, Entity[]>;
  /** map from id → entity */
  byId: Map<string, Entity>;
}

export type LoadState =
  | { status: "loading"; mode: Mode }
  | { status: "error"; mode: Mode; message: string }
  | { status: "ready"; mode: Mode; data: DatasetData };

/**
 * Loads the index file for the given mode and builds the lookup
 * maps used by the input-matching code. The geojson is loaded
 * independently by `<MapView>`.
 *
 * The hook keys all state by `mode` and resets to "loading" whenever
 * the mode changes, so callers don't see stale data from the previous
 * dataset while the new one is fetching.
 */
export function useDataset(mode: Mode): LoadState {
  const [state, setState] = useState<LoadState>({ status: "loading", mode });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", mode });
    const descriptor = DATASETS[mode];

    (async () => {
      try {
        const res = await fetch(descriptor.indexUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = (await res.json()) as DatasetIndex & {
          /** Pre-rename key from the very first preprocess version. */
          municipalities?: Entity[];
        };

        // Back-compat: an older preprocess wrote the lookup array
        // under `municipalities`; the current one uses `entities`.
        // Browsers that cached the old file would crash with
        // "raw.entities is not iterable" without this fallback.
        const list: Entity[] = raw.entities ?? raw.municipalities ?? [];
        if (!Array.isArray(list)) {
          throw new Error("Index file has no entities array");
        }
        // Normalise the in-memory raw object so the rest of the code
        // can rely on `entities` being present.
        if (!raw.entities) raw.entities = list;

        const byNormalizedName = new Map<string, Entity[]>();
        const byId = new Map<string, Entity>();
        const addLookup = (name: string, e: Entity) => {
          const key = normalize(name);
          if (!key) return;
          const bucket = byNormalizedName.get(key);
          if (bucket) {
            // Skip duplicates so typing one name only credits the
            // entity once even if name + alias normalise the same way.
            if (!bucket.includes(e)) bucket.push(e);
          } else {
            byNormalizedName.set(key, [e]);
          }
        };
        for (const e of list) {
          byId.set(e.id, e);
          addLookup(e.name, e);
          for (const alt of e.aliases ?? []) addLookup(alt, e);
        }
        if (cancelled) return;
        setState({
          status: "ready",
          mode,
          data: { descriptor, raw, byNormalizedName, byId },
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          mode,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  return state;
}
